import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Camera,
  CheckCircle,
  CreditCard,
  Loader2,
  Upload,
  XCircle,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useVerifyKyc } from "../../hooks/queries/useKyc";
import { resizeImageFile } from "../../utils/resizeImage";

const MAX_FILE_SIZE = 5 * 1024 * 1024;
// Phone cameras hand us 3–12 MB; the server refuses anything over 5 MB. 2048 px
// keeps a document legible for the verification vendor at a few hundred KB.
const KYC_MAX_SIDE = 2048;

const DECLINE_MESSAGES = {
  id_verification:
    "No se pudo verificar tu documento de identidad. Asegúrate de que la imagen sea clara y legible.",
  passive_liveness:
    "No se pudo verificar que la selfie sea de una persona real. Intenta con mejor iluminación.",
  face_match:
    "No se pudo confirmar que la selfie corresponda al documento. Asegúrate de que tu rostro sea claramente visible en ambas imágenes.",
};

const ImageInput = ({ id, label, icon: Icon, hint, capture, value, onChange }) => {
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const selectionId = useRef(0);

  useEffect(() => {
    const url = value ? URL.createObjectURL(value) : null;
    setPreview(url);
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [value]);

  useEffect(() => () => { selectionId.current += 1; }, []);

  const handleChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const currentSelection = ++selectionId.current;
    // A replacement must finish successfully before it can be submitted.
    onChange(null);
    setIsProcessing(false);
    if (!file.type.startsWith("image/")) {
      setError("El archivo debe ser una imagen.");
      return;
    }
    setError("");
    setIsProcessing(true);
    try {
      const blob = await resizeImageFile(file, { maxSide: KYC_MAX_SIDE, quality: 0.9 });
      if (currentSelection !== selectionId.current) return;
      if (blob.size > MAX_FILE_SIZE) throw new Error("La imagen excede el límite de 5 MB.");
      onChange(new File([blob], `${id}.jpg`, { type: "image/jpeg" }));
    } catch (err) {
      if (currentSelection !== selectionId.current) return;
      setError(err?.message || "No se pudo procesar la imagen.");
    } finally {
      if (currentSelection === selectionId.current) setIsProcessing(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-3">
      <label
        htmlFor={id}
        className="relative flex h-48 w-full cursor-pointer flex-col items-center justify-center rounded-2xl border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-blue-400 hover:bg-blue-50 overflow-hidden"
      >
        {isProcessing ? (
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        ) : preview ? (
          <img
            src={preview}
            alt="Vista previa"
            className="h-full w-full object-cover"
          />
        ) : (
          <>
            <Icon className="mb-2 h-10 w-10 text-gray-400" />
            <span className="text-sm font-medium text-gray-600">{label}</span>
            <span className="mt-1 text-xs text-gray-400">{hint}</span>
          </>
        )}
        <input
          id={id}
          aria-label={label}
          type="file"
          accept="image/*"
          capture={capture}
          className="hidden"
          onChange={handleChange}
        />
      </label>
      {error && <p className="text-xs text-red-600 font-medium">{error}</p>}
      {value && !error && (
        <p className="text-xs text-green-600 font-medium">
          Imagen lista ({(value.size / 1024 / 1024).toFixed(1)} MB)
        </p>
      )}
    </div>
  );
};

const KycVerificationPage = ({ onVerified }) => {
  const [frontImage, setFrontImage] = useState(null);
  const [selfieImage, setSelfieImage] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | submitting | approved | declined | error
  const [declineInfo, setDeclineInfo] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const mounted = useRef(false);
  const submitting = useRef(false);
  const redirectTimer = useRef(null);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      clearTimeout(redirectTimer.current);
    };
  }, []);

  const verifyKyc = useVerifyKyc();
  const { mutateAsync: submitVerification } = verifyKyc;

  const canSubmit = frontImage && selfieImage && status !== "submitting";

  const handleSubmit = useCallback(async () => {
    if (!frontImage || !selfieImage || submitting.current) return;
    submitting.current = true;

    setStatus("submitting");
    setDeclineInfo(null);
    setErrorMessage("");

    try {
      const result = await submitVerification({ frontImage, selfieImage });
      if (!mounted.current) return;

      if (result?.kyc_status === "approved") {
        setStatus("approved");
        redirectTimer.current = setTimeout(() => onVerified?.(result), 2000);
      } else if (result?.kyc_status === "declined") {
        setStatus("declined");
        setDeclineInfo(result.kyc_details);
      } else {
        throw new Error("No se pudo completar la verificación. Intenta de nuevo.");
      }
    } catch (err) {
      if (!mounted.current) return;
      setStatus("error");
      setErrorMessage(
        err?.message || "Ocurrió un error inesperado. Intenta de nuevo.",
      );
    } finally {
      submitting.current = false;
    }
  }, [frontImage, selfieImage, onVerified, submitVerification]);

  const handleRetry = () => {
    setFrontImage(null);
    setSelfieImage(null);
    setStatus("idle");
    setDeclineInfo(null);
    setErrorMessage("");
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-4 py-8">
      <motion.div
        className="w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl sm:p-8"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="mb-6 text-center">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-blue-100">
            <CreditCard className="h-7 w-7 text-blue-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-900">
            Verificación de Identidad
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            Para usar Athletain como scout, necesitamos verificar tu identidad.
          </p>
        </div>

        <AnimatePresence mode="wait">
          {status === "submitting" && (
            <motion.div
              key="submitting"
              className="flex flex-col items-center gap-4 py-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <Loader2 className="h-12 w-12 animate-spin text-blue-500" />
              <p className="text-sm font-medium text-gray-600">
                Verificando tu identidad...
              </p>
              <p className="text-xs text-gray-400">
                Esto puede tomar unos segundos
              </p>
            </motion.div>
          )}

          {status === "approved" && (
            <motion.div
              key="approved"
              className="flex flex-col items-center gap-4 py-12"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <CheckCircle className="h-16 w-16 text-green-500" />
              <p className="text-lg font-semibold text-green-700">
                ¡Identidad verificada!
              </p>
              <p className="text-sm text-gray-500">
                Redirigiendo al panel...
              </p>
            </motion.div>
          )}

          {status === "declined" && (
            <motion.div
              key="declined"
              className="flex flex-col items-center gap-4 py-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <XCircle className="h-14 w-14 text-red-500" />
              <p className="text-base font-semibold text-red-700">
                Verificación rechazada
              </p>
              <p className="text-center text-sm text-gray-600">
                {DECLINE_MESSAGES[declineInfo?.step] || "No se pudo completar la verificación."}
              </p>
              <button
                onClick={handleRetry}
                className="mt-2 rounded-xl bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
              >
                Intentar de nuevo
              </button>
            </motion.div>
          )}

          {status === "error" && (
            <motion.div
              key="error"
              className="flex flex-col items-center gap-4 py-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <XCircle className="h-14 w-14 text-amber-500" />
              <p className="text-base font-semibold text-amber-700">
                Error de conexión
              </p>
              <p className="text-center text-sm text-gray-600">
                {errorMessage}
              </p>
              <button
                onClick={handleRetry}
                className="mt-2 rounded-xl bg-blue-600 px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
              >
                Intentar de nuevo
              </button>
            </motion.div>
          )}

          {status === "idle" && (
            <motion.div
              key="form"
              className="flex flex-col gap-5"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <div>
                <h2 className="mb-2 text-sm font-semibold text-gray-700">
                  1. Documento de identidad
                </h2>
                <ImageInput
                  id="front_image"
                  label="Sube el frente de tu documento"
                  hint="Cédula, pasaporte u otro documento oficial"
                  icon={CreditCard}
                  capture="environment"
                  value={frontImage}
                  onChange={setFrontImage}
                />
              </div>

              <div>
                <h2 className="mb-2 text-sm font-semibold text-gray-700">
                  2. Selfie de verificación
                </h2>
                <ImageInput
                  id="selfie_image"
                  label="Toma una selfie clara"
                  hint="Buena iluminación, rostro completamente visible"
                  icon={Camera}
                  capture="user"
                  value={selfieImage}
                  onChange={setSelfieImage}
                />
              </div>

              <button
                disabled={!canSubmit}
                onClick={handleSubmit}
                className="mt-2 flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Upload className="h-4 w-4" />
                Enviar verificación
              </button>

              <p className="text-center text-xs text-gray-400">
                Tus datos se procesan de forma segura y se utilizan únicamente para
                verificar tu identidad.
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
};

export default KycVerificationPage;
